import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { extractApiErrorMessage } from "@/lib/api";
import PageShell from "@/components/PageShell";
import DataState from "@/components/DataState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Zap, ArrowLeft, Phone, MessageSquare, Info } from "lucide-react";
import useAutoDiscounts, { discountForService } from "@/hooks/useAutoDiscounts";

const LEAD_TEXT = (s) => {
  const lead = s.lead || {};
  if (lead.kind === "immediate_48h") return "متاحة بعد ٧٢ ساعة";
  if (lead.kind === "immediate_24h") return "متاحة فورًا — خلال يوم";
  if (lead.kind === "workdays") return `بعد ${lead.n} أيام عمل من اليوم`;
  if (lead.kind === "workweeks") return `بعد ${lead.n} أسابيع عمل من اليوم`;
  if (lead.kind === "calendar_days") {
    // n=0/1 collapses to the new 24h global floor — let the subtitle speak.
    if (!lead.n || lead.n <= 1) return "";
    return `بعد ~${lead.n} يومًا من اليوم`;
  }
  if (lead.kind === "earliest_date") return `أول موعد متاح حسب التقويم`;
  return "";
};

const ServiceCard = ({ s, onBook, autoDiscounts }) => {
  // Iteration 74 — admin can mark a service as "محجوزة بالكامل". The card
  // stays visible so customers understand the service exists, but the CTA
  // is disabled and swapped to a clear Arabic label.
  const fullyBooked = !!s.is_fully_booked;
  // Iteration 91 — surface active automatic discounts on the public
  // service card so customers see the offer before opening the booking
  // page. Uses the same `discountForService` classifier as the booking
  // page, so eligibility (all_sessions vs selected_sessions, bundles
  // excluded, active date range) is guaranteed identical.
  const auto = discountForService(s, autoDiscounts);
  return (
  <div className="card-warm p-7 flex flex-col h-full" data-testid={`service-card-${s.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <h3 className="font-heading text-2xl text-[#222222]">{s.title_ar}</h3>
        <p className="text-[#5E5C58] mt-1 text-sm">{s.subtitle_ar}</p>
      </div>
      {fullyBooked && (
        <Badge
          className="bg-[#B33939] hover:bg-[#B33939] text-white rounded-full"
          data-testid={`service-fully-booked-badge-${s.id}`}
        >
          محجوزة بالكامل
        </Badge>
      )}
      {!fullyBooked && s.is_immediate && (
        <Badge className="bg-[#B33939] hover:bg-[#B33939] text-white rounded-full">
          <Zap className="w-3 h-3 ms-1" /> فورية
        </Badge>
      )}
      {!fullyBooked && s.bundle && (
        <Badge className="bg-[#0A4D3C] hover:bg-[#0A4D3C] text-white rounded-full">
          باقة ×{s.bundle}
        </Badge>
      )}
    </div>

    <div className="mt-5 flex items-baseline gap-2 flex-wrap">
      {auto ? (
        <>
          <span
            className="font-heading text-xl text-[#A8A29E] line-through"
            data-testid={`service-price-original-${s.id}`}
          >
            {Number(auto.base).toLocaleString("ar-SA")}
          </span>
          <span
            className="font-heading text-3xl text-[#0A4D3C]"
            data-testid={`service-price-final-${s.id}`}
          >
            {Number(auto.final).toLocaleString("ar-SA")}
          </span>
          <span className="text-[#5E5C58] text-sm">ر.س</span>
          <Badge
            className="bg-[#0A4D3C]/10 hover:bg-[#0A4D3C]/10 text-[#0A4D3C] rounded-full text-xs"
            data-testid={`service-auto-discount-badge-${s.id}`}
          >
            خصم {auto.percentage}%
          </Badge>
        </>
      ) : (
        <>
          <span
            className="font-heading text-3xl text-[#0A4D3C]"
            data-testid={`service-price-final-${s.id}`}
          >
            {Number(s.price).toLocaleString("ar-SA")}
          </span>
          <span className="text-[#5E5C58] text-sm">ر.س</span>
        </>
      )}
    </div>

    <div className="mt-3 text-[#5E5C58] text-sm flex items-center gap-2">
      <Clock className="w-4 h-4" /> ساعة كاملة
    </div>
    {LEAD_TEXT(s) && (
      <div className="mt-1 text-[#0A4D3C] text-sm">• {LEAD_TEXT(s)}</div>
    )}
    {s.ui_note && (
      <div className="mt-3 text-xs text-[#5E5C58] bg-[#F6F4EE] border border-[#E5E1D8] rounded-xl p-3 leading-relaxed">
        <Info className="w-3.5 h-3.5 inline ms-1 text-[#0A4D3C]" /> {s.ui_note}
      </div>
    )}

    {fullyBooked ? (
      <Button
        data-testid={`book-service-btn-${s.id}`}
        disabled
        aria-disabled="true"
        className="mt-6 rounded-full bg-[#B33939] hover:bg-[#B33939] text-white self-start disabled:opacity-90 disabled:cursor-not-allowed"
      >
        محجوزة بالكامل
      </Button>
    ) : (
      <Button
        data-testid={`book-service-btn-${s.id}`}
        onClick={() => onBook(s)}
        className="mt-6 rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white self-start"
      >
        احجز الآن <ArrowLeft className="w-4 h-4 me-2" />
      </Button>
    )}
  </div>
  );
};

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  // Iteration 91 — surface active automatic discounts on session cards.
  const autoDiscounts = useAutoDiscounts();

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    api
      .get("/services")
      .then((r) => setServices((r.data || []).filter((x) => x.type === "consultation")))
      .catch((e) => setError(extractApiErrorMessage(e, "تعذّر تحميل الخدمات، يرجى المحاولة مرة أخرى")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const voice = services.filter((s) => s.modality === "voice");
  const text = services.filter((s) => s.modality === "text");

  const handleBook = (s) => {
    // Iteration 74 — defence in depth: never navigate to booking flow if
    // admin marked the service as fully booked (button is already disabled).
    if (s?.is_fully_booked) return;
    navigate(`/booking/${s.id}`);
  };

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="max-w-2xl">
          <h1 className="font-heading text-4xl sm:text-5xl text-[#222222]">الخدمات</h1>
          <p className="text-[#5E5C58] mt-4 leading-loose">
            جلسات صوتية ونصية تُجرى عبر تطبيق Botim. اختر الخدمة المناسبة ثم حدّد الموعد
            بما يتوافق مع مدة الحجز المحددة لكل نوع.
          </p>
        </div>

        <DataState
          loading={loading}
          error={error}
          onRetry={load}
          empty={!loading && services.length === 0}
          emptyText="لا توجد خدمات متاحة حاليًا"
          loadingText="جاري تحميل الخدمات..."
          testId="services-state"
        >
          <div className="mt-10">
            <div className="flex items-center gap-3 mb-2">
              <Phone className="w-5 h-5 text-[#0A4D3C]" />
              <h2 className="font-heading text-2xl text-[#222222]">الجلسات الصوتية</h2>
            </div>
            <p
              data-testid="services-voice-intro"
              className="text-sm text-[#5E5C58] leading-loose mb-5 max-w-3xl"
            >
              جلسة خاصة مع انا الخالدي لمدة ساعة لمناقشة الاوتاد
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid-voice">
              {voice.map((s) => (
                <ServiceCard key={s.id} s={s} onBook={handleBook} autoDiscounts={autoDiscounts} />
              ))}
            </div>
          </div>

          <div className="mt-14">
            <div className="flex items-center gap-3 mb-2">
              <MessageSquare className="w-5 h-5 text-[#0A4D3C]" />
              <h2 className="font-heading text-2xl text-[#222222]">جلسات الرسائل</h2>
            </div>
            {text.length > 0 && text[0].disclaimer && (
              <div
                data-testid="services-text-disclaimer"
                className="text-sm text-[#5E5C58] bg-[#FAF8F2] border border-[#E5E1D8] rounded-2xl p-4 leading-loose mb-5 max-w-3xl"
              >
                {text[0].disclaimer}
              </div>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid-text">
              {text.map((s) => (
                <ServiceCard key={s.id} s={s} onBook={handleBook} autoDiscounts={autoDiscounts} />
              ))}
            </div>
          </div>
        </DataState>
      </section>
    </PageShell>
  );
};

export default Services;
